/**
 * Hierarchy button component
 */
import {
  Component,
  OnDestroy,
  Input,
  OnChanges,
  HostListener,
  ElementRef,
  Output,
  EventEmitter,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { get, isEmpty } from 'lodash';
import { ISiteHierarchy } from '@shared/types';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'site-hierarchy-list-button',
  templateUrl: './hierarchy-list-button.component.html',
  styleUrls: ['./hierarchy-list-button.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XVISiteHierarchyListButtonComponent
  implements OnDestroy, OnChanges
{
  public cancelSubscription$: Subject<void> = new Subject<void>();
  @Input() public hierarchiesList: ISiteHierarchy[];
  // eslint-disable-next-line
  @Output() public onApply = new EventEmitter<any>();

  public itemsToDisplay: ISiteHierarchy[] = [];
  public searchControl: FormControl = new FormControl();
  public selectedItem: ISiteHierarchy = {} as ISiteHierarchy;
  public showItemsList: boolean = false;
  public activeHierarchy: ISiteHierarchy;
  public I18n: typeof I18n = I18n;

  public constructor(
    private elementRef: ElementRef,
    private i18nService: XVII18nService,
  ) {}

  @HostListener('document:click', ['$event'])
  public clickOut(event: MouseEvent): void {
    const clickedOutside = !this.elementRef.nativeElement.contains(
      event.target,
    );
    if (clickedOutside) {
      this.showItemsList = false;
    }
  }

  public ngOnChanges(changes): void {
    if ('hierarchiesList' in changes) {
      this.fullFillHierarchiesList();
    }
  }

  public fullFillHierarchiesList(): void {
    if (!isEmpty(this.hierarchiesList)) {
      this.itemsToDisplay = this.hierarchiesList;
      this.searchControl.valueChanges
        .pipe(takeUntil(this.cancelSubscription$))
        .subscribe((filterValue) => {
          this.itemsToDisplay = this.hierarchiesList.filter((item) =>
            item.name.match(new RegExp(filterValue, 'i')),
          );
        });
    }
  }

  public ngOnDestroy(): void {
    this.cancelSubscription$.next();
  }

  public resetClick(): void {
    this.searchControl.setValue('');
    this.selectedItem = {} as ISiteHierarchy;
    this.applyClick();
  }

  public hierarchyButtonClick(): void {
    this.showItemsList = !this.showItemsList;
  }

  public selectHierarchy(hierarchy): void {
    const { id } = hierarchy;
    // If item already selected, we should unselect it.
    if (get(this.selectedItem, 'id') === id) {
      this.selectedItem = {} as ISiteHierarchy;

      return;
    }

    this.selectedItem = hierarchy;
  }

  public isDisabled(item): boolean {
    const selectedId = get(this.selectedItem, 'id', null);

    return selectedId && selectedId !== item.id;
  }

  public applyClick(): void {
    this.onApply.emit(this.selectedItem);
    this.showItemsList = false;
  }

  public isSelected(item): boolean {
    return item.id === this.selectedItem.id;
  }

  public getBtnTitle(): string {
    // activeHierarchyName and whitespace maybe not used before, but keep them for i18n purpose
    return this.i18nService.instant(I18n.InfoMessages.HierarchyTitle, {
      activeHierarchyName: this?.activeHierarchy?.name || '',
      whitespace: this?.activeHierarchy?.name ? ' ' : '',
    });
  }
}
